/* eslint-disable @atlaskit/design-system/ensure-design-token-usage */
import React from 'react';
import last from 'lodash/last';
import ReactEchartsCore from 'echarts-for-react/lib/core';
import { LineChart, BarChart } from 'echarts/charts';
import { MarkLineComponent, TooltipComponent, GridComponent } from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { useIsReducedMotion } from '@atlaskit/motion';

import { colors } from '@atlaskit/theme';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import type { LineChartByEnvDataPoint, LineChartByEnvSeries } from '../../../common/types.tsx';
import {
	CHART_SPACE_LEFT,
	CHART_SPACE_RIGHT,
} from '../../../common/ui/line-chart-by-environment/constants.tsx';
import messages from './messages.tsx';
import { useTooltipFormatter } from './tooltip/index.tsx';

echarts.use([
	LineChart,
	BarChart,
	MarkLineComponent,
	TooltipComponent,
	GridComponent,
	CanvasRenderer,
]);

type Props = {
	series: LineChartByEnvSeries[];
};

export const Chart = ({ series }: Props) => {
	const { formatMessage, formatDate } = useIntl();
	const tooltipFormatter = useTooltipFormatter();
	const prefersReducedMotion = useIsReducedMotion();

	// RLSS-2812: support dark mode for echarts
	const axisCommonStyle = {
		nameGap: gridSize * 4,
		nameLocation: 'middle',
		nameTextStyle: {
			color: colors.N400,
			// This cannot be replaced with a token as ECharts requires a numeric pixel value.
			// Background: https://atlassian.slack.com/archives/C071DCS8MDL/p1733289846954599
			// eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
			fontSize: 12,
		},
	};

	const chartOptions = {
		animation: !prefersReducedMotion,
		tooltip: {
			backgroundColor: colors.N900,
			formatter: tooltipFormatter,
			transitionDuration: 0,
			trigger: 'axis',
			axisPointer: {
				type: 'line',
				lineStyle: {
					type: 'dashed',
					color: colors.B100,
					width: 0.5,
				},
			},
			confine: true,
		},
		grid: {
			left: CHART_SPACE_LEFT,
			right: CHART_SPACE_RIGHT,
			top: gridSize * 2,
			bottom: gridSize * 8,
		},
		xAxis: {
			// @ts-expect-error - TS2532 - Object is possibly 'undefined'.
			data: last(series).data.map(
				({ customInfo: { startDateInclusive } }: LineChartByEnvDataPoint) =>
					formatDate(startDateInclusive, {
						month: 'short',
						day: 'numeric',
					}),
			),
			axisLine: {
				lineStyle: {
					color: colors.N40,
					width: 2,
				},
			},
			axisTick: {
				alignWithLabel: true,
			},
			offset: gridSize * 2,
			...axisCommonStyle,
		},
		yAxis: {
			name: formatMessage(messages.yAxisLabel),
			...axisCommonStyle,
			axisLine: {
				show: false,
			},
			minInterval: 1,
			splitNumber: 4,
			axisTick: false,
			splitLine: {
				lineStyle: {
					color: colors.N30,
				},
			},
		},
		textStyle: {
			color: colors.N400,
		},
		backgroundColor: 'transparent',
		series,
	};

	return (
		<ReactEchartsCore
			echarts={echarts}
			option={chartOptions}
			// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={{ height: '100%' }}
		/>
	);
};
